import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Shield, Eye, EyeOff, User, Lock, Fingerprint,
    ChevronRight, Sparkles, CheckCircle2, AlertCircle, Loader2, ChevronLeft, Globe
} from 'lucide-react';
import { contactService } from '../services/contactService';
import { Contact } from '../types';
import { AVATARS, AVATAR_CATEGORIES, getAvatarsByCategory } from '../data/avatars';
import { getLocale, isRTL, SupportedLanguage } from '../locales';

interface AuthPageProps {
    onLogin: (user: Contact) => void;
    initialLang?: SupportedLanguage;
    onLanguageChange?: (lang: SupportedLanguage) => void;
}

const AuthPage: React.FC<AuthPageProps> = ({ onLogin, initialLang = 'en', onLanguageChange }) => {
    const [lang, setLang] = useState<SupportedLanguage>(initialLang);
    const t = getLocale(lang);
    const rtl = isRTL(lang);

    const toggleLanguage = () => {
        const newLang = lang === 'en' ? 'ar' : 'en';
        setLang(newLang);
        onLanguageChange?.(newLang);
    };

    const [mode, setMode] = useState<'LOGIN' | 'REGISTER'>('LOGIN');
    const [step, setStep] = useState<1 | 2 | 3>(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    // Fields
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [fullName, setFullName] = useState('');
    const [avatarUrl, setAvatarUrl] = useState('');
    const [avatarCategory, setAvatarCategory] = useState('all');
    const [showPassword, setShowPassword] = useState(false);

    // Get displayed avatars
    const displayedAvatars = getAvatarsByCategory(avatarCategory).slice(0, 12);

    const handleAction = async () => {
        setLoading(true);
        setError(null);

        const cleanUser = username.trim();
        const cleanPass = password.trim();

        try {
            if (mode === 'LOGIN') {
                // QUICK LOGIN BYPASS
                const finalUser = cleanUser || 'investor';
                const finalPass = cleanPass || 'naja2026';

                const result = await contactService.loginUser(finalUser, finalPass);
                if (result.success && result.user) {
                    setSuccess(true);
                    setTimeout(() => onLogin(result.user!), 800);
                } else {
                    setError(result.error || (lang === 'ar' ? 'بيانات غير صحيحة' : 'Invalid credentials'));
                }
            } else {
                if (step === 1) {
                    if (!fullName.trim()) {
                        setError(lang === 'ar' ? 'الرجاء إدخال اسمك' : 'Please enter your name');
                        setLoading(false);
                        return;
                    }
                    setStep(2);
                    setLoading(false);
                    return;
                }
                if (step === 2) {
                    if (!avatarUrl) {
                        setError(lang === 'ar' ? 'اختر صورة رمزية' : 'Please select an avatar');
                        setLoading(false);
                        return;
                    }
                    setStep(3);
                    setLoading(false);
                    return;
                }
                // Step 3: Final registration
                if (password !== confirmPassword) {
                    setError(lang === 'ar' ? 'كلمات المرور غير متطابقة' : 'Passcodes do not match');
                    setLoading(false);
                    return;
                }
                if (cleanUser.length < 3) {
                    setError(lang === 'ar' ? 'اسم المستخدم قصير جداً' : 'Username too short');
                    setLoading(false);
                    return;
                }
                const finalAvatar = avatarUrl || `https://api.dicebear.com/9.x/avataaars/svg?seed=${fullName}`;
                const result = await contactService.registerUser(fullName, cleanUser, cleanPass, finalAvatar);
                if (result.success && result.user) {
                    setSuccess(true);
                    setTimeout(() => onLogin(result.user!), 800);
                } else {
                    setError(result.error || 'Registration Failed');
                }
            }
        } catch (e: any) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const goBack = () => {
        if (step > 1) {
            setStep((step - 1) as 1 | 2 | 3);
            setError(null);
        }
    };

    // Mobile-optimized dark blue design
    return (
        <div className={`min-h-screen w-full flex flex-col overflow-hidden ${rtl ? 'font-[Cairo]' : ''}`}
            style={{
                background: 'linear-gradient(135deg, #0A1628 0%, #0F1F3A 50%, #1A2F4F 100%)',
                fontFamily: 'var(--font-sans)'
            }}>

            {/* NATURE-TECH BACKGROUND EFFECTS */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden">
                {/* Organic floating orbs */}
                <div className="absolute w-96 h-96 rounded-full blur-[120px] -top-32 -left-32 animate-breathe"
                    style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.15) 0%, transparent 70%)' }} />
                <div className="absolute w-64 h-64 rounded-full blur-[100px] bottom-20 right-0 animate-leaf-float"
                    style={{ background: 'radial-gradient(circle, rgba(96,165,250,0.2) 0%, transparent 70%)', animationDelay: '1s' }} />
                <div className="absolute w-48 h-48 rounded-full blur-[80px] top-1/2 left-1/4 animate-soft-pulse"
                    style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.12) 0%, transparent 70%)', animationDelay: '2s' }} />

                {/* Subtle grid for tech feel */}
                <div className="absolute inset-0 opacity-[0.02]" style={{
                    backgroundImage: `linear-gradient(var(--nature-sky) 0.5px, transparent 0.5px), linear-gradient(90deg, var(--nature-sky) 0.5px, transparent 0.5px)`,
                    backgroundSize: '48px 48px'
                }} />
            </div>

            {/* LANGUAGE TOGGLE */}
            <div className="absolute top-4 right-4 z-50">
                <button
                    onClick={toggleLanguage}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white/70 hover:text-white hover:bg-white/10 transition-all text-sm"
                >
                    <Globe className="w-4 h-4" />
                    {lang === 'en' ? 'عربي' : 'EN'}
                </button>
            </div>

            {/* SUCCESS OVERLAY */}
            <AnimatePresence>
                {success && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="fixed inset-0 z-[100] bg-[#0a1628]/95 flex items-center justify-center"
                    >
                        <motion.div
                            initial={{ scale: 0.5, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="text-center px-6"
                        >
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: 'spring', delay: 0.1 }}
                                className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-green-500/20 flex items-center justify-center shadow-[0_0_40px_rgba(34,197,94,0.3)]"
                            >
                                <CheckCircle2 className="w-10 h-10 text-green-400" />
                            </motion.div>
                            <h2 className="text-2xl font-bold text-white mb-2">
                                {lang === 'ar' ? 'تم التحقق بنجاح' : 'Access Granted'}
                            </h2>
                            <p className="text-cyan-400 text-sm">
                                {lang === 'ar' ? 'جاري تشغيل النظام...' : 'Initializing Guardian System...'}
                            </p>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* MAIN CONTENT - Full height mobile layout */}
            <div className="relative z-10 flex-1 flex flex-col px-6 py-4 safe-area-inset overflow-hidden">

                {/* HEADER - Organic & Welcoming */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`text-center ${mode === 'REGISTER' ? 'pt-6 pb-4' : 'pt-10 pb-8'}`}
                >
                    <motion.div
                        animate={{
                            scale: [1, 1.05, 1],
                            rotate: [0, 1, -1, 0]
                        }}
                        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                        className={`${mode === 'REGISTER' ? 'w-16 h-16 mb-3' : 'w-20 h-20 mb-5'} mx-auto rounded-3xl flex items-center justify-center relative overflow-hidden`}
                        style={{
                            background: 'linear-gradient(135deg, var(--nature-forest) 0%, var(--nature-sky) 100%)',
                            boxShadow: '0 0 40px rgba(96,165,250,0.4), inset 0 0 20px rgba(255,255,255,0.1)'
                        }}
                    >
                        {/* Animated background shimmer */}
                        <div className="absolute inset-0 animate-shimmer opacity-30" />
                        <Shield className={`${mode === 'REGISTER' ? 'w-8 h-8' : 'w-10 h-10'} text-white relative z-10`} strokeWidth={2} />
                    </motion.div>
                    <h1 className={`${mode === 'REGISTER' ? 'text-2xl' : 'text-3xl'} font-black tracking-tight mb-2`}
                        style={{ color: 'var(--text-primary)', letterSpacing: '0.02em' }}>
                        Protectly
                    </h1>
                    {mode === 'LOGIN' && (
                        <p className="text-xs font-semibold tracking-widest uppercase"
                            style={{ color: 'var(--nature-sky)', opacity: 0.8 }}>
                            {lang === 'ar' ? 'الحارس الرقمي' : 'Digital Guardian'}
                        </p>
                    )}
                </motion.div>

                {/* PROGRESS - Register only */}
                {mode === 'REGISTER' && (
                    <div className={`flex items-center justify-center gap-2 mb-6 ${rtl ? 'flex-row-reverse' : ''}`}>
                        {[1, 2, 3].map((s) => (
                            <div key={s} className="flex items-center">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${step >= s ? 'bg-blue-500 text-white' : 'bg-white/5 text-gray-500 border border-white/10'
                                    }`}>
                                    {step > s ? '✓' : s}
                                </div>
                                {s < 3 && <div className={`w-8 h-0.5 mx-1 ${step > s ? 'bg-blue-500' : 'bg-white/10'}`} />}
                            </div>
                        ))}
                    </div>
                )}

                {/* ERROR MESSAGE */}
                <AnimatePresence>
                    {error && (
                        <motion.div
                            initial={{ opacity: 0, y: -10, height: 0 }}
                            animate={{ opacity: 1, y: 0, height: 'auto' }}
                            exit={{ opacity: 0, y: -10, height: 0 }}
                            className="mb-4"
                        >
                            <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm p-3 rounded-xl flex items-center gap-2">
                                <AlertCircle className="w-4 h-4 shrink-0" />
                                <span>{error}</span>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* FORM CONTENT - Scrollable */}
                <div className="flex-1 overflow-y-auto">
                    <AnimatePresence mode="wait">

                        {/* LOGIN */}
                        {mode === 'LOGIN' && (
                            <motion.div
                                key="login"
                                initial={{ opacity: 0, x: rtl ? 20 : -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: rtl ? -20 : 20 }}
                                className="space-y-4"
                            >
                                <div className="text-center mb-6">
                                    <h2 className="text-xl font-bold text-white">
                                        {lang === 'ar' ? 'مرحباً بعودتك' : 'Welcome Back'}
                                    </h2>
                                    <p className="text-gray-400 text-sm">
                                        {lang === 'ar' ? 'سجل دخولك للمتابعة' : 'Sign in to continue'}
                                    </p>
                                </div>

                                {/* Username - Touch Optimized */}
                                <div>
                                    <label className="text-sm font-semibold block mb-2"
                                        style={{ color: 'var(--accent-primary)' }}>
                                        {lang === 'ar' ? 'اسم المستخدم' : 'Username'}
                                    </label>
                                    <div className="relative">
                                        <User className={`absolute ${rtl ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 w-5 h-5 transition-colors`}
                                            style={{ color: 'var(--accent-primary)', opacity: 0.5 }} />
                                        <input
                                            className={`w-full border-2 rounded-2xl ${rtl ? 'pr-14 pl-4' : 'pl-14 pr-4'} transition-all duration-300 text-base`}
                                            style={{
                                                backgroundColor: 'var(--bg-card)',
                                                borderColor: 'rgba(96,165,250,0.2)',
                                                color: 'var(--text-primary)',
                                                height: '56px', // Larger touch target
                                                fontSize: 'var(--text-base)'
                                            }}
                                            placeholder={lang === 'ar' ? 'أدخل اسم المستخدم' : 'Enter username'}
                                            value={username}
                                            onChange={e => setUsername(e.target.value)}
                                            onFocus={(e) => e.target.style.borderColor = 'var(--accent-primary)'}
                                            onBlur={(e) => e.target.style.borderColor = 'rgba(96,165,250,0.2)'}
                                            dir={rtl ? 'rtl' : 'ltr'}
                                        />
                                    </div>
                                </div>

                                {/* Password */}
                                <div>
                                    <label className="text-xs font-bold text-blue-300 tracking-wider block mb-2 uppercase">
                                        {lang === 'ar' ? 'كلمة المرور' : 'Passcode'}
                                    </label>
                                    <div className="relative">
                                        <Lock className={`absolute ${rtl ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 w-5 h-5 text-blue-400/50`} />
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            className={`w-full bg-[#0d1f3c] border border-blue-500/20 rounded-2xl ${rtl ? 'pr-12 pl-14' : 'pl-12 pr-14'} py-4 text-white text-base focus:border-blue-500 focus:outline-none transition-all`}
                                            placeholder="••••••••"
                                            value={password}
                                            onChange={e => setPassword(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && handleAction()}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className={`absolute ${rtl ? 'left-4' : 'right-4'} top-1/2 -translate-y-1/2 text-gray-500 hover:text-white p-1`}
                                        >
                                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {/* REGISTER STEP 1: Name */}
                        {mode === 'REGISTER' && step === 1 && (
                            <motion.div
                                key="reg-1"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                className="space-y-4"
                            >
                                <div className="text-center mb-6">
                                    <h2 className="text-xl font-bold text-white">
                                        {lang === 'ar' ? 'ما اسمك؟' : "What's your name?"}
                                    </h2>
                                    <p className="text-gray-400 text-sm">
                                        {lang === 'ar' ? 'هذا الاسم سيظهر للآخرين' : 'This will be your display name'}
                                    </p>
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-blue-300 tracking-wider block mb-2 uppercase">
                                        {lang === 'ar' ? 'الاسم الكامل' : 'Full Name'}
                                    </label>
                                    <input
                                        className="w-full bg-[#0d1f3c] border border-blue-500/20 rounded-2xl px-4 py-4 text-white text-base focus:border-blue-500 focus:outline-none transition-all"
                                        placeholder={lang === 'ar' ? 'أدخل اسمك الكامل' : 'Enter your full name'}
                                        value={fullName}
                                        onChange={e => setFullName(e.target.value)}
                                        autoFocus
                                        dir={rtl ? 'rtl' : 'ltr'}
                                    />
                                </div>
                            </motion.div>
                        )}

                        {/* REGISTER STEP 2: Avatar */}
                        {mode === 'REGISTER' && step === 2 && (
                            <motion.div
                                key="reg-2"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                className="flex flex-col h-full"
                            >
                                <div className="text-center mb-3">
                                    <h2 className="text-lg font-bold text-white">
                                        {lang === 'ar' ? 'اختر صورتك' : 'Choose Avatar'}
                                    </h2>
                                </div>

                                {/* Category Pills - Compact */}
                                <div className="flex gap-2 overflow-x-auto pb-2 mb-3 scrollbar-hide">
                                    {AVATAR_CATEGORIES.slice(0, 5).map((cat) => (
                                        <button
                                            key={cat.id}
                                            onClick={() => setAvatarCategory(cat.id)}
                                            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${avatarCategory === cat.id
                                                ? 'bg-blue-500 text-white'
                                                : 'bg-white/5 text-gray-400 border border-white/10'
                                                }`}
                                        >
                                            {cat.icon} {cat.label}
                                        </button>
                                    ))}
                                </div>

                                {/* Avatar Grid - Fixed height for mobile */}
                                <div className="grid grid-cols-3 gap-2 overflow-y-auto flex-1 min-h-0 max-h-[35vh] p-1">
                                    {displayedAvatars.map((avatar) => (
                                        <motion.button
                                            key={avatar.id}
                                            whileTap={{ scale: 0.95 }}
                                            onClick={() => setAvatarUrl(avatar.url)}
                                            className={`relative aspect-square rounded-xl overflow-hidden transition-all ${avatarUrl === avatar.url
                                                ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-[#0a1628] shadow-[0_0_15px_rgba(59,130,246,0.4)]'
                                                : 'opacity-70 active:opacity-100'
                                                }`}
                                        >
                                            <img
                                                src={avatar.url}
                                                alt={avatar.name}
                                                className="w-full h-full object-cover"
                                                loading="lazy"
                                            />
                                            {avatarUrl === avatar.url && (
                                                <div className="absolute inset-0 bg-blue-500/30 flex items-center justify-center">
                                                    <CheckCircle2 className="w-6 h-6 text-white drop-shadow-lg" />
                                                </div>
                                            )}
                                        </motion.button>
                                    ))}
                                </div>

                                {/* Selected Preview - Compact */}
                                {avatarUrl && (
                                    <div className={`flex items-center gap-3 mt-3 p-2 bg-blue-500/10 border border-blue-500/20 rounded-xl ${rtl ? 'flex-row-reverse' : ''}`}>
                                        <img src={avatarUrl} className="w-10 h-10 rounded-lg object-cover" alt="Selected" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-white font-medium text-sm truncate">{fullName}</p>
                                            <p className="text-blue-300/70 text-xs">
                                                {lang === 'ar' ? 'تم الاختيار ✓' : 'Selected ✓'}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </motion.div>
                        )}

                        {/* REGISTER STEP 3: Credentials */}
                        {mode === 'REGISTER' && step === 3 && (
                            <motion.div
                                key="reg-3"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                className="space-y-4"
                            >
                                <div className="text-center mb-6">
                                    <h2 className="text-xl font-bold text-white">
                                        {lang === 'ar' ? 'تأمين الحساب' : 'Secure Your Account'}
                                    </h2>
                                    <p className="text-gray-400 text-sm">
                                        {lang === 'ar' ? 'أنشئ بيانات الدخول' : 'Create your login credentials'}
                                    </p>
                                </div>

                                <div>
                                    <label className="text-xs font-bold text-blue-300 tracking-wider block mb-2 uppercase">
                                        {lang === 'ar' ? 'اسم المستخدم' : 'Username'}
                                    </label>
                                    <input
                                        className="w-full bg-[#0d1f3c] border border-blue-500/20 rounded-2xl px-4 py-4 text-white text-base focus:border-blue-500 focus:outline-none transition-all"
                                        placeholder={lang === 'ar' ? 'اختر اسم مستخدم' : 'Choose a username'}
                                        value={username}
                                        onChange={e => setUsername(e.target.value)}
                                        dir={rtl ? 'rtl' : 'ltr'}
                                    />
                                </div>

                                <div>
                                    <label className="text-xs font-bold text-blue-300 tracking-wider block mb-2 uppercase">
                                        {lang === 'ar' ? 'كلمة المرور' : 'Passcode'}
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            className="w-full bg-[#0d1f3c] border border-blue-500/20 rounded-2xl px-4 py-4 text-white text-base focus:border-blue-500 focus:outline-none transition-all pr-12"
                                            placeholder={lang === 'ar' ? 'أنشئ كلمة مرور' : 'Create a passcode'}
                                            value={password}
                                            onChange={e => setPassword(e.target.value)}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500"
                                        >
                                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="text-xs font-bold text-blue-300 tracking-wider block mb-2 uppercase">
                                        {lang === 'ar' ? 'تأكيد كلمة المرور' : 'Confirm Passcode'}
                                    </label>
                                    <input
                                        type="password"
                                        className="w-full bg-[#0d1f3c] border border-blue-500/20 rounded-2xl px-4 py-4 text-white text-base focus:border-blue-500 focus:outline-none transition-all"
                                        placeholder={lang === 'ar' ? 'أعد كتابة كلمة المرور' : 'Confirm your passcode'}
                                        value={confirmPassword}
                                        onChange={e => setConfirmPassword(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && handleAction()}
                                    />
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* BOTTOM ACTIONS - Fixed at bottom */}
                <div className="pt-4 pb-2 space-y-3">
                    {/* Back button for registration */}
                    {mode === 'REGISTER' && step > 1 && (
                        <button
                            onClick={goBack}
                            className={`flex items-center gap-2 text-gray-400 hover:text-white text-sm py-2 ${rtl ? 'flex-row-reverse mr-auto' : 'ml-0'}`}
                        >
                            <ChevronLeft className="w-4 h-4" />
                            {lang === 'ar' ? 'رجوع' : 'Go Back'}
                        </button>
                    )}

                    {/* Main action button - Touch Optimized */}
                    <motion.button
                        onClick={handleAction}
                        disabled={loading}
                        whileTap={{ scale: loading ? 1 : 0.96 }}
                        whileHover={{ scale: 1.01 }}
                        className="w-full rounded-3xl font-bold text-lg tracking-wide transition-all relative overflow-hidden disabled:opacity-50"
                        style={{
                            background: 'linear-gradient(135deg, var(--nature-forest) 0%, var(--nature-sky) 100%)',
                            height: '60px', // Large touch target
                            boxShadow: '0 8px 30px rgba(96,165,250,0.3), inset 0 1px 0 rgba(255,255,255,0.2)',
                            color: '#FFFFFF'
                        }}
                    >
                        {/* Ripple effect background */}
                        {!loading && (
                            <motion.div
                                className="absolute inset-0 bg-white/10"
                                initial={{ scale: 0, opacity: 0 }}
                                whileTap={{ scale: 2, opacity: [0, 0.3, 0] }}
                                transition={{ duration: 0.5 }}
                            />
                        )}

                        <span className={`flex items-center justify-center gap-3 relative z-10 ${rtl ? 'flex-row-reverse' : ''}`}>
                            {loading ? (
                                <>
                                    <Loader2 className="w-6 h-6 animate-spin" />
                                    <span className="font-semibold">{lang === 'ar' ? 'جاري التحقق...' : 'Authenticating...'}</span>
                                </>
                            ) : mode === 'LOGIN' ? (
                                <>
                                    <Fingerprint className="w-6 h-6" />
                                    <span className="font-bold">{lang === 'ar' ? 'دخول' : 'Sign In'}</span>
                                </>
                            ) : step < 3 ? (
                                <>
                                    <span className="font-bold">{lang === 'ar' ? 'متابعة' : 'Continue'}</span>
                                    <ChevronRight className="w-6 h-6" />
                                </>
                            ) : (
                                <>
                                    <Sparkles className="w-6 h-6" />
                                    <span className="font-bold">{lang === 'ar' ? 'إنشاء الحساب' : 'Create Account'}</span>
                                </>
                            )}
                        </span>
                    </motion.button>

                    {/* Toggle mode */}
                    <div className="text-center pt-2">
                        <button
                            onClick={() => {
                                setMode(mode === 'LOGIN' ? 'REGISTER' : 'LOGIN');
                                setStep(1);
                                setError(null);
                            }}
                            className="text-gray-400 hover:text-white text-sm transition-colors"
                        >
                            {mode === 'LOGIN' ? (
                                <>{lang === 'ar' ? 'مستخدم جديد؟' : 'New user?'} <span className="text-blue-400 font-medium">{lang === 'ar' ? 'سجل هنا' : 'Register'}</span></>
                            ) : (
                                <>{lang === 'ar' ? 'لديك حساب؟' : 'Have account?'} <span className="text-blue-400 font-medium">{lang === 'ar' ? 'دخول' : 'Login'}</span></>
                            )}
                        </button>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default AuthPage;
